import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { MapPin, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Heading, Text, Stack } from '@/components/ui/index.js';
import { useVenueStore } from '@/features/venues/store/useVenueStore.js';
import styles from './SimilarVenues.module.css';

/**
 * SimilarVenues - Horizontal scroll of similar venue cards
 */
export function SimilarVenues({ currentVenueId }) {
  const navigate = useNavigate();
  const { getSimilarVenues } = useVenueStore();
  const [venues, setVenues] = useState([]);
  
  useEffect(() => {
    const similar = getSimilarVenues(currentVenueId, 5);
    setVenues(similar);
  }, [currentVenueId, getSimilarVenues]);
  
  const handleVenueClick = (venueId) => {
    navigate(`/venues/${venueId}`);
  };
  
  if (venues.length === 0) {
    return null;
  }
  
  return (
    <Stack direction="column" gap={4}>
      <Heading level={2} size="lg" color="primary">
        Similar Venues Nearby
      </Heading>
      
      <div className={styles.scrollContainer}>
        <div className={styles.cardsWrapper}>
          {venues.map((venue, index) => (
            <VenueCard
              key={venue.id}
              venue={venue}
              onClick={() => handleVenueClick(venue.id)}
              index={index}
            />
          ))}
        </div>
      </div>
    </Stack>
  );
}

/**
 * Individual mini venue card
 */
function VenueCard({ venue, onClick, index }) {
  return (
    <button
      onClick={onClick}
      className={styles.card}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      {/* Image Placeholder */}
      <div className={styles.image}>
        <div className={styles.imagePlaceholder}>
          <span className={styles.sportEmoji}>
            {getSportEmoji(venue.sports[0]?.name)}
          </span>
        </div>
      </div>
      
      {/* Content */}
      <div className={styles.content}>
        <Text weight="semibold" color="primary" className={styles.name}>
          {venue.name}
        </Text>
        
        <div className={styles.meta}>
          <div className={styles.rating}>
            <Star size={12} className={styles.star} fill="currentColor" />
            <Text size="sm" color="secondary">{venue.rating}</Text>
          </div>
          
          <div className={styles.location}>
            <MapPin size={12} />
            <Text size="sm" color="muted">{venue.distance}</Text>
          </div>
        </div>
        
        <div className={styles.sports}>
          {venue.sports.slice(0, 2).map((sport) => (
            <span key={sport.name} className={styles.sportTag}>
              {sport.name}
            </span>
          ))}
          {venue.sports.length > 2 && (
            <span className={styles.sportTag}>
              +{venue.sports.length - 2}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

/**
 * Get emoji for sport
 */
function getSportEmoji(sportName) {
  const emojis = {
    'Basketball': '🏀',
    'Volleyball': '🏐',
    'Badminton': '🏸',
    'Tennis': '🎾',
    'Soccer': '⚽',
    'Football': '🏈',
    'Baseball': '⚾',
    'Swimming': '🏊',
  };
  return emojis[sportName] || '🏆';
}

SimilarVenues.propTypes = {
  currentVenueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

VenueCard.propTypes = {
  venue: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    rating: PropTypes.number.isRequired,
    distance: PropTypes.string.isRequired,
    sports: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
};

export default SimilarVenues;
